import { useCallback, useEffect, useMemo, useState, Fragment as React_Fragment } from "react";
import api from "@/lib/api";
import Topbar from "@/components/layout/Topbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  CheckCircle, Circle, Truck, Package, ArrowsClockwise, User,
  ClipboardText, Warning, CalendarBlank, Clock, CaretRight, CaretDown,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const fmtKg = (n) => `${Number(n || 0).toLocaleString("es-AR", { minimumFractionDigits: 0, maximumFractionDigits: 3 })} kg`;
const todayISO = () => new Date().toISOString().slice(0, 10);

export default function ControlSalidaPage() {
  const fecha = todayISO(); // fijo a hoy, sin filtro
  const [incluirPickup, setIncluirPickup] = useState(false);
  const [data, setData] = useState({ pedidos: [], choferes: [], total_pedidos: 0, total_controlados: 0 });
  const [loading, setLoading] = useState(false);
  const [choferFiltro, setChoferFiltro] = useState("todos");
  const [savingIds, setSavingIds] = useState(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/control-salida", {
        params: { fecha, incluir_pickup: incluirPickup ? "true" : "false" },
      });
      setData(data || { pedidos: [], choferes: [] });
    } finally { setLoading(false); }
  }, [fecha, incluirPickup]);

  useEffect(() => { load(); }, [load]);

  const pedidos = useMemo(() => {
    if (choferFiltro === "todos") return data.pedidos;
    return data.pedidos.filter((p) => p.chofer === choferFiltro);
  }, [data.pedidos, choferFiltro]);

  // Agrupar por chofer y, dentro de cada chofer, por salida_nro (cuando el
  // chofer tiene 2+ salidas configuradas en Logística vía puntos de corte).
  // Estructura devuelta: [ [chofer, Viaje[]], ... ]
  const grupos = useMemo(() => {
    const byChofer = new Map();
    for (const p of pedidos) {
      const c = p.chofer || "Sin asignar";
      if (!byChofer.has(c)) byChofer.set(c, new Map());
      const viajes = byChofer.get(c);
      // Preferimos salida_nro (splits desde Logística) sobre viaje_clave (grupo por localidad).
      const key = p.salida_nro != null
        ? `salida-${p.salida_nro}`
        : (p.viaje_id || p.viaje_clave || "__sin_viaje__");
      if (!viajes.has(key)) {
        viajes.set(key, {
          key,
          salida_nro: p.salida_nro || null,
          clave: p.viaje_clave || null,
          items: [],
        });
      }
      viajes.get(key).items.push(p);
    }
    return Array.from(byChofer.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([chofer, viajesMap]) => {
        const viajes = Array.from(viajesMap.values()).sort((a, b) => {
          if (a.salida_nro && b.salida_nro) return a.salida_nro - b.salida_nro;
          return (a.clave || "zzz").localeCompare(b.clave || "zzz");
        });
        return [chofer, viajes];
      });
  }, [pedidos]);

  const toggleCheck = async (p) => {
    const idKey = `${p.source_type}:${p.source_id}`;
    setSavingIds((s) => new Set([...s, idKey]));
    try {
      await api.post("/control-salida/check", {
        source_type: p.source_type,
        source_id: p.source_id,
        fecha_envio: data.fecha_envio,
        ok: !p.control_ok,
      });
      // Actualizo estado local sin refetch total
      setData((d) => ({
        ...d,
        pedidos: d.pedidos.map((x) =>
          x.source_type === p.source_type && x.source_id === p.source_id
            ? { ...x, control_ok: !p.control_ok, control_at: new Date().toISOString() }
            : x
        ),
      }));
      toast.success(!p.control_ok ? "Marcado como controlado ✓" : "Desmarcado");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al guardar");
    } finally {
      setSavingIds((s) => { const n = new Set(s); n.delete(idKey); return n; });
    }
  };

  const controlados = data.pedidos.filter((p) => p.control_ok).length;
  const pendientes = data.pedidos.length - controlados;

  // ─── HISTÓRICO ────────────────────────────────────────────────────────
  const [tab, setTab] = useState("hoy");
  const [histDesde, setHistDesde] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [histHasta, setHistHasta] = useState(todayISO());
  const [histUsuario, setHistUsuario] = useState("todos");
  const [historial, setHistorial] = useState({ grupos: [], usuarios_disponibles: [], total_controles: 0 });
  const [histLoading, setHistLoading] = useState(false);
  const [expandedHist, setExpandedHist] = useState({}); // { row.id: true } → despliega items
  const toggleHistRow = (rid) => setExpandedHist((s) => ({ ...s, [rid]: !s[rid] }));

  const loadHistorial = useCallback(async () => {
    setHistLoading(true);
    try {
      const params = { desde: histDesde, hasta: histHasta };
      if (histUsuario !== "todos") params.usuario = histUsuario;
      const { data } = await api.get("/control-salida/historial", { params });
      setHistorial(data || { grupos: [], usuarios_disponibles: [], total_controles: 0 });
    } finally { setHistLoading(false); }
  }, [histDesde, histHasta, histUsuario]);

  useEffect(() => {
    if (tab === "historial") loadHistorial();
  }, [tab, loadHistorial]);

  const fmtTime = (iso) => iso ? new Date(iso).toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" }) : "-";
  const fmtDateLong = (d) => new Date(d + "T12:00:00").toLocaleDateString("es-AR", { weekday: "long", day: "2-digit", month: "long", year: "numeric" });

  return (
    <>
      <Topbar
        title="Control de Salida"
        subtitle="Chequeo final antes de cargar la camioneta · pedidos producidos ordenados por hoja de ruta"
        right={
          <Button variant="outline" onClick={tab === "hoy" ? load : loadHistorial} disabled={loading || histLoading} className="rounded-sm h-9" data-testid="cs-refresh">
            <ArrowsClockwise size={13} className={`mr-1 ${(loading || histLoading) ? "animate-spin" : ""}`} /> Actualizar
          </Button>
        }
      />

      <div className="p-4">
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="rounded-sm">
            <TabsTrigger value="hoy" data-testid="cs-tab-hoy" className="rounded-sm">Del día</TabsTrigger>
            <TabsTrigger value="historial" data-testid="cs-tab-historial" className="rounded-sm">Histórico</TabsTrigger>
          </TabsList>

          <TabsContent value="hoy" className="space-y-4 mt-3">
        {/* Contenido */}
        {loading && (
          <div className="text-center py-10 text-neutral-500">Cargando…</div>
        )}
        {!loading && data.pedidos.length === 0 && (
          <div className="bg-white border border-[#E5E5E5] rounded-sm p-10 text-center text-neutral-500">
            <ClipboardText size={38} weight="duotone" className="mx-auto mb-2 text-neutral-300" />
            No hay pedidos terminados de producir para esta fecha.
          </div>
        )}
        {!loading && data.pedidos.length > 0 && grupos.map(([chofer, viajes]) => {
          const allItems = viajes.flatMap((v) => v.items);
          const totalKg = allItems.reduce((s, p) => s + Number(p.total_kg_producidos || p.total_kg_esperados || 0), 0);
          const ok = allItems.filter((p) => p.control_ok).length;
          const hasMultipleViajes = viajes.length > 1;
          return (
            <div key={chofer} className="bg-white border border-[#E5E5E5] rounded-sm overflow-hidden">
              <div className="bg-neutral-50 border-b border-[#E5E5E5] px-4 py-2 flex items-center gap-3">
                <Truck size={16} className="text-[#6B1F24]" />
                <div className="font-heading font-semibold text-[14px]">{chofer}</div>
                <div className="text-[11px] font-mono text-neutral-500">
                  {ok}/{allItems.length} controlados · {fmtKg(totalKg)}
                </div>
                {hasMultipleViajes && (
                  <span className="text-[10px] font-mono uppercase bg-amber-100 text-amber-800 rounded px-1.5 py-0.5 border border-amber-200" data-testid={`cs-multi-viaje-${chofer}`}>
                    {viajes.length} salidas
                  </span>
                )}
                <div className="ml-auto text-[11px] font-mono text-neutral-500">
                  {allItems.length} parada{allItems.length !== 1 ? "s" : ""}
                </div>
              </div>
              {viajes.map((v, vidx) => {
                const items = v.items;
                const vKg = items.reduce((s, p) => s + Number(p.total_kg_producidos || p.total_kg_esperados || 0), 0);
                const vOk = items.filter((p) => p.control_ok).length;
                return (
                  <div key={v.key} data-testid={`cs-viaje-${chofer}-${v.key}`}>
                    {hasMultipleViajes && (
                      <div className={`px-4 py-1.5 flex items-center gap-2 text-[12px] ${vidx === 0 ? "" : "border-t border-[#E5E5E5]"} bg-amber-50/70 border-b border-amber-100`}>
                        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-600 text-white text-[10px] font-mono font-bold">
                          {vidx + 1}
                        </span>
                        <span className="font-heading font-semibold text-amber-900">
                          Salida {v.salida_nro || (vidx + 1)}
                          {v.clave ? ` · ${v.clave}` : ""}
                        </span>
                        <span className="text-[10px] font-mono text-amber-800/70">
                          {vOk}/{items.length} · {fmtKg(vKg)}
                        </span>
                      </div>
                    )}
                    <div className="divide-y divide-neutral-100">
                {items.map((p) => {
                  const idKey = `${p.source_type}:${p.source_id}`;
                  const saving = savingIds.has(idKey);
                  return (
                    <div
                      key={idKey}
                      className={`px-4 py-3 grid grid-cols-[40px_60px_1fr_2fr_140px] gap-3 items-start ${p.control_ok ? "bg-emerald-50/40" : ""}`}
                      data-testid={`cs-pedido-${idKey}`}
                    >
                      {/* Orden en la ruta */}
                      <div className="text-center">
                        <div className="text-[10px] font-mono text-neutral-400 uppercase">Orden</div>
                        <div className="text-lg font-heading font-bold text-[#6B1F24]">
                          {p.orden_chofer < 9999 ? p.orden_chofer : "—"}
                        </div>
                      </div>
                      {/* Nº pedido */}
                      <div>
                        <div className="text-[10px] font-mono text-neutral-400 uppercase">N°</div>
                        <div className="text-[13px] font-mono font-bold">{p.numero_pedido}</div>
                      </div>
                      {/* Cliente */}
                      <div>
                        <div className="text-[10px] font-mono text-neutral-400 uppercase">Cliente</div>
                        <div className="text-[14px] font-medium flex items-center gap-1">
                          <User size={13} className="text-neutral-400" /> {p.cliente_nombre}
                        </div>
                        {p.observaciones_pedido && (
                          <div className="text-[11px] italic text-amber-700 mt-1 flex items-start gap-1">
                            <Warning size={11} className="mt-[2px]" /> {p.observaciones_pedido}
                          </div>
                        )}
                      </div>
                      {/* Items */}
                      <div>
                        <div className="text-[10px] font-mono text-neutral-400 uppercase mb-1">
                          Productos ({fmtKg(p.total_kg_producidos || p.total_kg_esperados)})
                        </div>
                        <div className="space-y-1">
                          {p.items.map((it, i) => {
                            const kgProd = it.kg_producidos;
                            const kgEsp = it.kg_esperados;
                            const diff = kgProd != null ? (Number(kgProd) - Number(kgEsp)) : null;
                            return (
                              <div key={i} className="grid grid-cols-[1fr_140px] gap-2 text-[12px] items-start">
                                <div>
                                  <div className="flex items-center gap-1.5">
                                    {it.rubro_color && (
                                      <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: it.rubro_color }} />
                                    )}
                                    <span>{it.producto}</span>
                                    {it.sin_stock && (
                                      <span className="ml-1 text-[10px] uppercase font-mono bg-rose-100 text-rose-700 rounded px-1">
                                        sin stock
                                      </span>
                                    )}
                                    {it.terminado && !it.sin_stock && (
                                      <CheckCircle size={11} weight="fill" className="text-emerald-500 ml-1" />
                                    )}
                                  </div>
                                  {it.especificacion && (
                                    <div className="text-[11px] italic text-amber-700 ml-3">↳ {it.especificacion}</div>
                                  )}
                                </div>
                                <div className="text-right font-mono text-[11px]">
                                  {kgProd != null && (
                                    <>
                                      <span className="font-bold text-neutral-800">{fmtKg(kgProd)}</span>
                                      {diff != null && Math.abs(diff) > 0.01 && (
                                        <span className={`ml-1 text-[10px] ${diff < 0 ? "text-rose-600" : "text-emerald-600"}`}>
                                          ({diff >= 0 ? "+" : ""}{diff.toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })})
                                        </span>
                                      )}
                                      <div className="text-neutral-400 text-[10px]">esp: {fmtKg(kgEsp)}</div>
                                    </>
                                  )}
                                  {kgProd == null && (
                                    <span className="text-neutral-400">esp: {fmtKg(kgEsp)}</span>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      {/* Botón check */}
                      <div className="flex flex-col items-end gap-1">
                        <Button
                          onClick={() => toggleCheck(p)}
                          disabled={saving}
                          className={`rounded-sm w-full h-10 ${p.control_ok
                            ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                            : "bg-white border border-[#6B1F24] text-[#6B1F24] hover:bg-[#F5F1E9]"}`}
                          data-testid={`cs-check-${idKey}`}
                        >
                          {p.control_ok ? (
                            <><CheckCircle size={16} weight="fill" className="mr-1" /> Controlado</>
                          ) : (
                            <><Circle size={16} className="mr-1" /> Marcar controlado</>
                          )}
                        </Button>
                        {p.control_ok && p.control_by_username && (
                          <div className="text-[10px] font-mono text-emerald-700 text-right">
                            por {p.control_by_username}
                            {p.control_at && (
                              <div className="text-neutral-500">
                                {new Date(p.control_at).toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" })}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
                  </div>
                );
              })}
            </div>
          );
        })}
          </TabsContent>

          <TabsContent value="historial" className="space-y-4 mt-3">
            {/* Filtros del histórico */}
            <div className="bg-white border border-[#E5E5E5] rounded-sm p-3 flex flex-wrap items-end gap-3">
              <div>
                <Label className="text-[11px] uppercase font-mono text-neutral-500">Desde</Label>
                <Input type="date" value={histDesde} onChange={(e) => setHistDesde(e.target.value)}
                  className="rounded-sm h-9 mt-1 w-40 font-mono" data-testid="cs-hist-desde" />
              </div>
              <div>
                <Label className="text-[11px] uppercase font-mono text-neutral-500">Hasta</Label>
                <Input type="date" value={histHasta} onChange={(e) => setHistHasta(e.target.value)}
                  className="rounded-sm h-9 mt-1 w-40 font-mono" data-testid="cs-hist-hasta" />
              </div>
              <div>
                <Label className="text-[11px] uppercase font-mono text-neutral-500">Usuario</Label>
                <Select value={histUsuario} onValueChange={setHistUsuario}>
                  <SelectTrigger className="rounded-sm h-9 mt-1 w-52" data-testid="cs-hist-usuario">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos los usuarios</SelectItem>
                    {historial.usuarios_disponibles.map((u) => (
                      <SelectItem key={u} value={u}>{u}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={loadHistorial} disabled={histLoading} className="rounded-sm h-9 bg-[#6B1F24] hover:bg-[#571519]" data-testid="cs-hist-buscar">
                {histLoading ? "Buscando…" : "Buscar"}
              </Button>
              <div className="ml-auto border border-neutral-200 rounded-sm px-3 py-1.5 min-w-[130px]">
                <div className="text-[9px] uppercase font-mono text-neutral-500">Total controles</div>
                <div className="text-lg font-heading font-bold" data-testid="cs-hist-total">{historial.total_controles}</div>
              </div>
            </div>

            {/* Tabla agrupada por fecha */}
            {histLoading && <div className="text-center py-10 text-neutral-500">Cargando…</div>}
            {!histLoading && historial.grupos.length === 0 && (
              <div className="bg-white border border-[#E5E5E5] rounded-sm p-10 text-center text-neutral-500">
                <ClipboardText size={38} weight="duotone" className="mx-auto mb-2 text-neutral-300" />
                No hay controles registrados en el rango seleccionado.
              </div>
            )}
            {!histLoading && historial.grupos.map((g) => (
              <div key={g.fecha_envio} className="bg-white border border-[#E5E5E5] rounded-sm overflow-hidden" data-testid={`cs-hist-grupo-${g.fecha_envio}`}>
                <div className="bg-neutral-50 border-b border-[#E5E5E5] px-4 py-2 flex items-center gap-3 flex-wrap">
                  <CalendarBlank size={16} className="text-[#6B1F24]" />
                  <div className="font-heading font-semibold text-[14px] capitalize">{fmtDateLong(g.fecha_envio)}</div>
                  <div className="text-[11px] font-mono text-neutral-500">
                    · {g.total} control{g.total !== 1 ? "es" : ""}
                  </div>
                  <div className="ml-auto flex gap-1.5 flex-wrap">
                    {g.usuarios.map((u) => (
                      <span key={u.username} className="text-[10px] font-mono bg-[#F5F1E9] text-[#6B1F24] px-2 py-0.5 rounded-sm">
                        <User size={9} weight="bold" className="inline mr-0.5" /> {u.username} · {u.count}
                      </span>
                    ))}
                  </div>
                </div>
                <table className="w-full text-[12px]">
                  <thead className="bg-neutral-50/50 text-[10px] uppercase font-mono text-[#666]">
                    <tr>
                      <th className="px-2 py-1.5 w-6"></th>
                      <th className="px-4 py-1.5 text-left w-24">Hora</th>
                      <th className="px-4 py-1.5 text-left w-32">Nº Pedido</th>
                      <th className="px-4 py-1.5 text-left">Cliente</th>
                      <th className="px-4 py-1.5 text-left w-40">Controlado por</th>
                      <th className="px-4 py-1.5 text-left">Observaciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.rows.map((r) => {
                      const items = r.items || [];
                      const isExp = !!expandedHist[r.id];
                      return (
                        <React_Fragment key={r.id}>
                          <tr
                            className={`border-t border-neutral-100 ${items.length ? "cursor-pointer hover:bg-neutral-50" : ""}`}
                            onClick={() => items.length && toggleHistRow(r.id)}
                            data-testid={`cs-hist-row-${r.id}`}
                          >
                            <td className="px-2 py-1.5 text-center text-neutral-400">
                              {items.length ? (isExp ? <CaretDown size={12} weight="bold" /> : <CaretRight size={12} weight="bold" />) : null}
                            </td>
                            <td className="px-4 py-1.5 font-mono text-neutral-600">
                              <Clock size={11} className="inline mr-1 text-neutral-400" />{fmtTime(r.at)}
                            </td>
                            <td className="px-4 py-1.5 font-mono font-bold">
                              {r.numero_pedido}
                              {items.length > 0 && (
                                <span className="ml-1 text-[10px] font-normal text-neutral-400">
                                  · {items.length} ítem{items.length !== 1 ? "s" : ""}
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-1.5">{r.cliente_nombre || <span className="text-neutral-400 italic">—</span>}</td>
                            <td className="px-4 py-1.5">
                              <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-sm">
                                <CheckCircle size={11} weight="fill" /> {r.by_username}
                              </span>
                            </td>
                            <td className="px-4 py-1.5 text-[11px] italic text-neutral-500">
                              {r.observaciones || "—"}
                            </td>
                          </tr>
                          {isExp && items.length > 0 && (
                            <tr className="bg-[#FBF7EF]/40 border-t border-neutral-100" data-testid={`cs-hist-items-${r.id}`}>
                              <td colSpan={6} className="px-4 py-2">
                                <table className="w-full text-[11px]" data-testid={`cs-hist-items-table-${r.id}`}>
                                  <thead className="text-[9px] uppercase font-mono text-neutral-500">
                                    <tr>
                                      <th className="text-left px-2 py-1">Producto</th>
                                      <th className="text-left px-2 py-1 w-16">SKU</th>
                                      <th className="text-right px-2 py-1 w-20">Kg esp.</th>
                                      <th className="text-right px-2 py-1 w-20">Kg prod.</th>
                                      <th className="text-left px-2 py-1 w-44">Producido por</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {items.map((it, i) => (
                                      <tr key={i} className="border-t border-neutral-100" data-testid={`cs-hist-item-${r.id}-${i}`}>
                                        <td className="px-2 py-1">
                                          <span>{it.producto}</span>
                                          {it.sin_stock && (
                                            <span className="ml-1 text-[9px] uppercase font-mono bg-rose-100 text-rose-700 rounded px-1">
                                              sin stock
                                            </span>
                                          )}
                                          {it.terminado && !it.sin_stock && (
                                            <CheckCircle size={10} weight="fill" className="text-emerald-500 inline ml-1" />
                                          )}
                                        </td>
                                        <td className="px-2 py-1 font-mono text-neutral-500">{it.sku || "—"}</td>
                                        <td className="px-2 py-1 text-right font-mono text-neutral-500">{fmtKg(it.kg_esperados)}</td>
                                        <td className="px-2 py-1 text-right font-mono font-bold">
                                          {it.kg_producidos != null ? fmtKg(it.kg_producidos) : <span className="text-neutral-400 italic">—</span>}
                                        </td>
                                        <td className="px-2 py-1">
                                          {it.producido_por ? (
                                            <span className="inline-flex items-center gap-1 bg-[#F5F1E9] text-[#6B1F24] px-2 py-0.5 rounded-sm font-mono text-[10px]">
                                              <User size={9} weight="bold" /> {it.producido_por}
                                              {it.producido_at && (
                                                <span className="text-neutral-500 ml-1">
                                                  {new Date(it.producido_at).toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" })}
                                                </span>
                                              )}
                                            </span>
                                          ) : (
                                            <span className="text-neutral-400 italic text-[10px]">sin marcar</span>
                                          )}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </td>
                            </tr>
                          )}
                        </React_Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ))}
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
}
